import {
  ASK_IMAGE_BYTES_MAX,
  ASK_IMAGES_MAX,
  ASK_SET_UP_FIRST_MESSAGES,
  ASK_TURN_MARKER,
  askScopeAdoProject,
  isAskSetUpFirstCode,
  isToolboxRole,
  type AskEngineErrorCode,
  type AskProduct,
  type AskScope,
  type AskStatusState,
  type AskVerdict,
  type ToolboxPersona,
  type ToolboxRole,
} from "@ttc-spectree/toolbox-contract";
import { plainMessage, type HelpNotReadyCopy } from "@/components/toolbox/help/help-not-ready";
import type { ToolboxAccessResponse } from "@/lib/api/toolbox-access";
import { personasOn, projectAccessOf } from "@/hooks/queries/use-toolbox-access";
import { helpDisplayText, helpResetTime } from "@/lib/toolbox/help";
import type { ChipTone } from "@/components/grooming/grooming-chips";

/**
 * Pure helpers for the SpecTree Toolbox Product Q&A page (epic
 * PI-SPEC-SW-E310, feature PI-SPEC-SW-1628, ER-1667 Package F). Kept free of
 * React so the page, the conversation hook and the tests share one definition
 * of each rule. Help's out-of-date verdict, Toolbox-only rules and Send to
 * Aaron are not part of Product Q&A.
 */

/** A stable key for one product scope (the picker's option value). FogBugz scopes key on their ids. */
export function askScopeKey(scope: AskScope): string {
  return scope.kind === "ado-project"
    ? `ado:${scope.project}`
    : `fogbugz:${String(scope.fogbugzProjectId)}:${scope.fogbugzAreaId !== undefined ? String(scope.fogbugzAreaId) : ""}:${scope.adoProject}`;
}

/** The answer text the page may show: the `SPECTREE_ASK_TURN` marker line is never shown, even half streamed. */
export function askDisplayText(text: string, streaming = false): string {
  return helpDisplayText(text, streaming, ASK_TURN_MARKER);
}

/**
 * The person's roles and persona sent with a question, for the product's ADO
 * project: their roles there plus `admin` for a global Admin. Role ids, so
 * the skill reads stable values.
 */
export function askContextFor(
  access: ToolboxAccessResponse | null | undefined,
  scope: AskScope,
): { roles: ToolboxRole[]; persona?: ToolboxPersona } {
  const project = askScopeAdoProject(scope);
  const roles = new Set<ToolboxRole>();
  if (access?.isAdmin) roles.add("admin");
  for (const role of projectAccessOf(access, project)?.roles ?? []) {
    if (isToolboxRole(role)) roles.add(role);
  }
  const { persona } = personasOn(access, project);
  return { roles: [...roles], ...(persona ? { persona } : {}) };
}

// ---------------------------------------------------------------------------
// Product readiness (checkProduct)
// ---------------------------------------------------------------------------

/**
 * Where the chosen product stands on this computer:
 * - `checking`: `ask.checkProduct` has not answered yet;
 * - `ready`: the chat may be shown;
 * - `set-up-first`: a fixed sentence (`ASK_SET_UP_FIRST_MESSAGES`) and the way to Setup, never the ask box;
 * - `not-now`: anything else (repositories updating, a failed check): the message and Check again.
 */
export type AskProductReadiness =
  | { state: "checking" }
  | { state: "ready" }
  | { state: "set-up-first"; errorCode: AskEngineErrorCode; message: string }
  | { state: "not-now"; errorCode: AskEngineErrorCode | null; message: string };

/** Readiness from a `checkProduct` failure. Set-up codes always read their fixed sentence. Pure; exported for tests. */
export function askReadinessFromFailure(errorCode: AskEngineErrorCode, message: string | undefined): AskProductReadiness {
  if (isAskSetUpFirstCode(errorCode)) {
    return { state: "set-up-first", errorCode, message: ASK_SET_UP_FIRST_MESSAGES[errorCode] };
  }
  return { state: "not-now", errorCode, message: askErrorMessage(errorCode, message) };
}

/** A product whose ADO project has no repositories needs no check: it cannot be ready. Pure; exported for tests. */
export function askReadinessFromProduct(product: AskProduct): AskProductReadiness | null {
  return product.reposMapped ? null : askReadinessFromFailure("REPO_NOT_MAPPED", undefined);
}

// ---------------------------------------------------------------------------
// Messages
// ---------------------------------------------------------------------------

const IMAGE_MB = Math.round(ASK_IMAGE_BYTES_MAX / (1024 * 1024));

/** Shown when a fourth screenshot is added. */
export const ASK_IMAGE_LIMIT_MESSAGE = `You can attach up to ${String(ASK_IMAGES_MAX)} screenshots. Remove one to add another.`;

/** Shown when a screenshot is above the size cap. */
export const ASK_IMAGE_SIZE_MESSAGE = `Each screenshot can be up to ${String(IMAGE_MB)} MB. That one is larger.`;

/** Shown for a file that is not an accepted image type. */
export const ASK_IMAGE_TYPE_MESSAGE = "Product Q&A reads PNG, JPEG, WebP and GIF images.";

/** One plain sentence per engine error code; the typed question is kept so it can be asked again. */
export function askErrorMessage(code: AskEngineErrorCode | null | undefined, engineMessage?: string): string {
  if (code && isAskSetUpFirstCode(code)) return ASK_SET_UP_FIRST_MESSAGES[code];
  switch (code) {
    case "USAGE_LIMIT":
      return engineMessage?.trim()
        ? engineMessage.trim()
        : "Your Claude plan's usage limit is reached. Ask again once it resets.";
    case "NETWORK":
      return "Product Q&A could not connect. Check your connection, then ask again.";
    case "BUSY":
      return "Product Q&A is still answering another question. Wait for it to finish, then ask again.";
    case "NO_TURN_RETURNED":
      return "Claude did not finish its answer. Ask again.";
    case "TURN_TIMEOUT":
      return "Claude took too long to answer. Ask again, or ask a narrower question.";
    case "REPOS_UPDATING":
      return "This product's code is being updated on this computer. Try again in a moment.";
    case "IMAGE_TOO_LARGE":
      return ASK_IMAGE_SIZE_MESSAGE;
    case "TOO_MANY_IMAGES":
      return ASK_IMAGE_LIMIT_MESSAGE;
    case "STOPPED":
      return "Stopped.";
    default:
      return engineMessage?.trim() ? engineMessage.trim() : "Something went wrong. Ask again.";
  }
}

/** Product Q&A's words for a not-ready engine state, for Help's not-ready card and the disabled ask box. Pure. */
export function askNotReadyCopy(status: { state: AskStatusState; resetsAt?: string | undefined; message?: string | undefined }): HelpNotReadyCopy {
  switch (status.state) {
    case "signed-out":
      return {
        title: "Product Q&A needs Claude to answer",
        body: "Claude is signed out on this computer, so Product Q&A cannot answer right now. Sign in again and it is ready.",
        askReason: "Product Q&A can answer again once Claude is signed in",
        footer: "Answers come back as soon as Claude is signed in.",
      };
    case "claude-missing":
      return {
        title: "Product Q&A needs Claude to answer",
        body: "Claude Code is not installed on this computer, so Product Q&A cannot answer right now. Setup installs it.",
        askReason: "Product Q&A can answer again once Claude Code is installed",
        footer: "Answers come back as soon as Claude Code is installed.",
      };
    case "usage-limit": {
      const time = helpResetTime(status.resetsAt);
      return {
        title: "Product Q&A needs Claude to answer",
        body: time
          ? `Your Claude plan's usage limit is reached, so Product Q&A cannot answer right now. Your Claude plan resets at ${time}.`
          : "Your Claude plan's usage limit is reached, so Product Q&A cannot answer right now. It answers again when your plan resets.",
        askReason: time ? `Product Q&A can answer again at ${time}` : "Product Q&A can answer again when your Claude plan resets",
        footer: "Answers come back as soon as your Claude plan resets.",
      };
    }
    case "plugin-missing":
      return {
        title: "Product Q&A's files are missing",
        body: "This copy of SpecTree Toolbox is missing the files Product Q&A needs, so it cannot answer right now. Install the latest version to fix it.",
        askReason: "Product Q&A can answer again once the Toolbox is updated",
        footer: "Answers come back once the Toolbox is updated.",
      };
    default:
      return {
        title: "Product Q&A cannot answer right now",
        body: plainMessage(status.message, "Product Q&A cannot answer right now. Try again in a moment."),
        askReason: "Product Q&A can answer again once Claude is ready",
        footer: "Answers come back as soon as Claude is ready.",
      };
  }
}

// ---------------------------------------------------------------------------
// Verdicts
// ---------------------------------------------------------------------------

/** The chip tone of each verdict: spec agreement reads by `specAgrees`. Pure; exported for tests. */
export function askVerdictTone(verdict: AskVerdict, specAgrees?: "yes" | "no" | "partly"): ChipTone {
  switch (verdict) {
    case "answered-from-code":
      return "cur";
    case "spec-found":
      return specAgrees === "no" ? "red" : specAgrees === "partly" ? "warn" : "ok";
    case "code-only":
      return "warn";
    case "not-sure":
    case "off-topic":
      return "none";
  }
}

/** The plain warning under a `code-only` answer (epic decision on the verdict split). */
export const ASK_CODE_ONLY_WARNING =
  "No written spec was found, so nobody has written down what this is supposed to do. This is what the code does today.";

/** The plain line under a `spec-found` answer whose code and spec disagree (`specAgrees: "no"`). */
export const ASK_SPEC_DISAGREES_LINE = "The code and the written spec disagree. This may be a bug.";

/** The line of an off-topic answer about SpecTree Toolbox itself (`redirect: "help"`). */
export const ASK_HELP_REDIRECT_LINE = "This is a question about SpecTree Toolbox. Help answers those.";
